import { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'

interface InstructionsProps {
  onBack: () => void
}

export default function TestCaseGeneratorInstructions({ onBack: _onBack }: InstructionsProps) {
  const [activeSection, setActiveSection] = useState<string>('overview')

  const scrollToSection = (id: string, updateHash = true) => {
    const element = document.getElementById(id)
    if (element) {
      const offset = 80
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth',
      })
      setActiveSection(id)
      if (updateHash) {
        window.history.replaceState(null, '', `#${id}`)
      }
    }
  }

  useEffect(() => {
    const hash = window.location.hash.slice(1)
    if (hash) {
      setTimeout(() => scrollToSection(hash, false), 100)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'quick-start', 'input-methods', 'understanding-output', 'workflows', 'golden-examples', 'tips']
      const offset = 100
      let current = activeSection

      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 50) {
        current = sections[sections.length - 1]
      } else {
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId)
          if (element) {
            const rect = element.getBoundingClientRect()
            if (rect.top <= offset && rect.bottom > offset) {
              current = sectionId
              break
            }
          }
        }
      }

      if (current !== activeSection) {
        setActiveSection(current)
        window.history.replaceState(null, '', `#${current}`)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [activeSection])

  const breadcrumbs = [
    { label: 'Test Case Generator', href: '/test-case/' },
    { label: 'User Guide' },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px] mx-auto">
        {/* Two Column Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Sidebar Navigation */}
          <aside className="col-span-3">
            <div className="sticky top-20">
              <nav className="card p-4">
                <div className="text-xs font-medium text-linear-text-muted uppercase tracking-wider mb-4 px-2">
                  On This Page
                </div>
                <div className="space-y-1">
                  {[
                    { id: 'overview', label: 'Overview' },
                    { id: 'quick-start', label: 'Quick Start' },
                    { id: 'input-methods', label: 'Input Methods' },
                    { id: 'understanding-output', label: 'Understanding Output' },
                    { id: 'workflows', label: 'Common Workflows' },
                    { id: 'golden-examples', label: 'Golden Examples' },
                    { id: 'tips', label: 'Tips & Best Practices' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                        activeSection === item.id
                          ? 'bg-linear-accent text-white font-medium'
                          : 'text-linear-text-secondary hover:bg-linear-bg-hover hover:text-linear-text'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="col-span-9">
            <div className="space-y-8">
              {/* Header */}
              <div className="mb-2">
                <h1 className="text-2xl font-display font-bold text-linear-text mb-3">Test Case Generator</h1>
                <p className="text-linear-text-secondary text-base">
                  Turn your requirements into structured test suites in seconds. Feed in user stories and acceptance criteria, and the AI builds test cases you can run or import right away.
                </p>
              </div>

              {/* Overview */}
              <section id="overview" className="scroll-mt-20">
                <div className="card">
                  <h2 className="text-xl font-display font-bold text-linear-text mb-4">Overview</h2>
                  <div className="space-y-4">
                    <p className="text-linear-text-secondary leading-relaxed">
                      Drop in text descriptions, upload screenshots, or paste a Figma URL. The AI reads your input and generates structured test cases you can execute or import into your test management tool.
                    </p>
                    <p className="text-linear-text-secondary leading-relaxed">
                      You control how many test cases get generated (3 to 20), the step detail level (Concise, Standard, or Detailed), target devices &amp; browsers, and custom instructions that override defaults (like combining scenarios into fewer, longer cases). Results stream in real time so you can watch progress. And once they're done, you can edit inline, star your favorites as reusable templates, export to CSV, JSON or Markdown, share via link, or fork existing cases to tweak and regenerate.
                    </p>
                  </div>
                </div>
              </section>

              {/* Quick Start */}
              <section id="quick-start" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Quick Start</h2>
                  </div>
                  <div className="space-y-3">
                    {[
                      {
                        num: 1,
                        title: 'Define the Scope',
                        desc: 'Type a high-level description of the feature or flow you need to test in the main prompt area.',
                      },
                      {
                        num: 2,
                        title: 'Add Context (Jira, screenshots, Figma URL)',
                        desc: 'Import a Jira ticket, upload screenshots, or drop in a Figma URL. Richer context helps the AI spot UI elements and potential regression points it would otherwise miss.',
                      },
                      {
                        num: 3,
                        title: 'Configure Options (test count, detail level, custom instructions)',
                        desc: 'Dial in your output: set the target test count (or flip on "Let AI decide" and let it match the count to your requirement complexity), detail level, devices, browsers, and custom instructions. These settings shape how test cases come out.',
                      },
                      {
                        num: 4,
                        title: 'Generate',
                        desc: 'Hit Generate and watch the AI build your suite in real time.',
                      },
                      {
                        num: 5,
                        title: 'Review & Export',
                        desc: 'Check the generated steps, resolve any [VERIFY] tags, select the test cases you want, and export to CSV or your test management tool.',
                      },
                    ].map((step) => (
                      <div key={step.num} className="flex items-start gap-4 p-4 rounded-lg bg-linear-bg-input border border-linear-border hover:border-linear-border-input transition-colors">
                        <div className="flex-shrink-0 w-8 h-8 bg-linear-accent rounded-lg flex items-center justify-center font-bold text-white text-sm">
                          {step.num}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-linear-text mb-1">{step.title}</h3>
                          <p className="text-linear-text-muted text-sm">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Input Methods */}
              <section id="input-methods" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-info/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Input Methods</h2>
                  </div>

                  <div className="space-y-6">
                    {/* Text Input */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Text Input</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        Paste your requirements straight into the text area. Works best for:
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'User stories pulled from JIRA or similar tools',
                          'Short feature specs',
                          'Acceptance criteria lists',
                          'Bug reproduction steps you want to formalize into test cases',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border overflow-hidden">
                        <div className="bg-linear-bg-hover px-4 py-2 border-b border-linear-border">
                          <span className="text-xs font-medium text-linear-text-secondary">Example: Good Text Input</span>
                        </div>
                        <div className="p-4">
                          <pre className="text-sm text-linear-text-secondary leading-relaxed font-mono whitespace-pre-wrap">
{`As a user, I want to log in to my account so that
I can access protected features.

Acceptance Criteria:
- User can enter username and password
- Invalid credentials show error message
- Successful login redirects to dashboard
- Password must be masked
- Remember me option persists session`}
                          </pre>
                        </div>
                      </div>
                    </div>

                    {/* Jira Import */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg width="20" height="20" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.664 1H10.2603C10.2603 3.99443 12.7697 6.43176 15.8527 6.43176H18.147V8.55567C18.147 11.5501 20.6565 13.9875 23.7395 13.9875V2.04457C23.7395 1.45265 23.2735 1 22.664 1Z" fill="#2684FF"/>
                          <path d="M16.5339 6.98877H4.13013C4.13013 9.98321 6.63956 12.4205 9.72258 12.4205H12.0169V14.5793C12.0169 17.5737 14.5264 20.0111 17.6094 20.0111V8.03329C17.6094 7.47623 17.1433 6.98877 16.5339 6.98877Z" fill="url(#jira-instr-grad-1)"/>
                          <path d="M10.4038 13.0125H0C0 16.0069 2.50941 18.4442 5.59243 18.4442H7.88677V20.5682C7.88677 23.5626 10.3962 26 13.4792 26V14.057C13.4792 13.4651 12.9774 13.0125 12.4038 13.0125Z" fill="url(#jira-instr-grad-2)"/>
                          <defs>
                            <linearGradient id="jira-instr-grad-1" x1="17.3455" y1="7.01882" x2="12.2302" y2="12.45" gradientUnits="userSpaceOnUse">
                              <stop offset="0.176" stopColor="#0052CC"/>
                              <stop offset="1" stopColor="#2684FF"/>
                            </linearGradient>
                            <linearGradient id="jira-instr-grad-2" x1="11.5601" y1="13.0525" x2="5.63562" y2="18.9874" gradientUnits="userSpaceOnUse">
                              <stop offset="0.176" stopColor="#0052CC"/>
                              <stop offset="1" stopColor="#2684FF"/>
                            </linearGradient>
                          </defs>
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Jira Import</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        Pull requirements straight from your Jira instance instead of copy-pasting descriptions by hand. Find what you need 3 ways: type an issue key like PROJ-123, search by keyword, or paste a full URL.
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'Results show each ticket\'s key, summary, status and type so you can pick the right one fast',
                          'Toggle chips let you include the description, attachments, or both before importing',
                          'Image attachments (PNG, JPEG, GIF) get passed to the AI as visual input, same as uploading screenshots',
                          'A preview card shows the full ticket details before you commit to importing it',
                          'Works alongside text input, screenshots and Figma URLs for combined context',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border overflow-hidden">
                        <div className="bg-linear-bg-hover px-4 py-2 border-b border-linear-border">
                          <span className="text-xs font-medium text-linear-text-secondary">How It Works</span>
                        </div>
                        <div className="p-4 space-y-3 text-sm text-linear-text-muted">
                          <p>Type a ticket key, keyword, or URL into the Jira field. Matching tickets appear in a dropdown. Each result has toggle chips for Description and Attachments, so you control what the AI sees before clicking <strong className="text-linear-text">Import Ticket</strong>.</p>
                          <p>Once imported, the ticket locks in as a confirmed chip showing the key, summary and status. The AI builds test cases from whatever fields you toggled on, and you can still add text or screenshots on top for extra context.</p>
                        </div>
                      </div>

                      <div className="mt-4 bg-linear-bg-card rounded-lg border border-linear-border p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-semibold text-linear-text mb-2">Jira Connection Required</div>
                            <p className="text-sm text-linear-text-muted">
                              You need a connected Jira account to use this feature. If you haven't set one up yet, you'll see a banner in the Jira field with a link to the Settings page. Connection takes about 30 seconds.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Figma URL */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg width="20" height="20" viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" fill="#1ABCFE"/>
                          <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" fill="#0ACF83"/>
                          <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z" fill="#FF7262"/>
                          <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z" fill="#F24E1E"/>
                          <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z" fill="#A259FF"/>
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Figma URL</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        Paste any Figma URL (designs, boards, prototypes, or slides) to generate test cases from your mockups. The tool extracts:
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'A quick summary of UI components with counts and examples',
                          'Content themes detected in the design',
                          'Testing angles based on the components it finds',
                          'Component relationships that suggest interaction flows worth testing',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border overflow-hidden">
                        <div className="bg-linear-bg-hover px-4 py-2 border-b border-linear-border">
                          <span className="text-xs font-medium text-linear-text-secondary">Example: Figma Summary Output</span>
                        </div>
                        <div className="p-4">
                          <pre className="text-sm text-linear-text-secondary leading-relaxed font-mono whitespace-pre-wrap">
{`## Figma Design: Offer Details Page

### Component Summary
- 5 button(s) (examples: CTA Button, Close, Learn More)
- 3 input field(s) (examples: ZIP Code, Email)
- 12 text element(s)

### Content Themes
- Offers and promotions
- Pricing/monetary values

### Testing Considerations
- Form validation and data entry workflows
- Button interactions and state management`}
                          </pre>
                        </div>
                      </div>
                    </div>

                    {/* Figma Visual Accuracy Note */}
                    <div className="flex items-start gap-2.5 p-3 rounded-lg bg-info/10 border border-info/30">
                      <svg className="w-4 h-4 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-info leading-relaxed">
                        <span className="font-semibold">Visual content accuracy:</span> When the tool renders your Figma frames as images, the AI interprets them visually. If text in your design is small, decorative, or partially visible, the AI will describe it generically (e.g., "large decorative text in the footer") rather than guess the specific wording. Always verify that generated test cases reference the correct content from your designs.
                      </p>
                    </div>

                    {/* Image Upload */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Image Upload</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        Upload screenshots or UI mockups and the AI will build test cases from what it sees. Best for:
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'Screenshots of existing UI when you need regression test coverage',
                          'Wireframes and mockups from design tools',
                          'Multi-screen flows where you want cross-page test scenarios (up to 3 images)',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-semibold text-linear-text mb-2">Image Requirements</div>
                            <ul className="space-y-1 text-sm text-linear-text-muted">
                              <li>Formats: PNG, JPEG, GIF, WebP</li>
                              <li>Up to 3 images per generation</li>
                              <li>Pair with text input for richer context</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Device & Browser Targeting */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Device & Browser Targeting</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        Under "Additional Configuration", pick the devices and browsers you care about. The AI will shape prerequisites, steps and expected results to match those platforms.
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'Devices: All Devices, Desktop, Tablet or Mobile (multi-select)',
                          'Browsers: All Browsers, Chrome, Safari, Firefox or Microsoft Edge (multi-select)',
                          'Picking "All" resets individual selections. Deselecting everything reverts to "All"',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-semibold text-linear-text mb-2">Tip</div>
                            <p className="text-sm text-linear-text-muted">
                              Pick specific devices and browsers when you need platform-aware test steps
                              (e.g. "Verify responsive layout on Mobile Safari"). Leave it on "All" for
                              platform-neutral test cases.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Custom Instructions */}
                    <div className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                      <div className="flex items-center gap-3 mb-4">
                        <svg className="w-5 h-5 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-linear-text">Custom Instructions</h3>
                      </div>
                      <p className="text-linear-text-secondary mb-4">
                        The Custom Instructions field in Output Settings lets you override how the AI generates test cases. Whatever you type here takes priority over the built-in step count and structure defaults.
                      </p>
                      <div className="grid grid-cols-1 gap-2 mb-5">
                        {[
                          'Override step counts: "Use 10+ steps per test case" overrides the detail level default',
                          'Combine scenarios: "Combine related scenarios into fewer, longer test cases"',
                          'Focus areas: "Focus on accessibility and keyboard navigation testing"',
                          'Structure preferences: "Include cleanup/teardown steps in every test case"',
                          'You get 2,000 characters. Instructions persist when you Clone & Edit a suite',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>

                      <div className="bg-linear-bg-card rounded-lg border border-linear-border p-4">
                        <div className="flex items-start gap-3">
                          <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-semibold text-linear-text mb-2">When to use Custom Instructions</div>
                            <p className="text-sm text-linear-text-muted">
                              Custom Instructions are most useful when the default detail levels don't fit what you're doing. Say you want longer end-to-end cases that cover multiple user flows in a single test, or you need the AI to prioritize a specific testing approach like accessibility. Leave the field blank to stick with the standard defaults for your selected detail level.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Understanding Output */}
              <section id="understanding-output" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Understanding Output</h2>
                  </div>

                  <div className="space-y-6">
                    {/* Streaming Terminal */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <span className="w-2 h-2 bg-success rounded-full animate-pulse"></span>
                        Streaming Terminal Output
                      </h3>
                      <p className="text-linear-text-secondary mb-4 text-sm">
                        Test cases show up in real time inside a dark terminal-style display as the AI writes them.
                        You can watch progress and stop generation if something looks off. Clicking "Cancel Generation"
                        kills the run and deletes the partial job, so it won't clutter your history.
                      </p>
                      <div className="rounded-lg overflow-hidden border border-linear-border">
                        <div className="bg-linear-bg-hover px-4 py-2 flex items-center gap-2 border-b border-linear-border">
                          <div className="flex gap-1.5">
                            <div className="w-3 h-3 rounded-full bg-error"></div>
                            <div className="w-3 h-3 rounded-full bg-warning"></div>
                            <div className="w-3 h-3 rounded-full bg-success"></div>
                          </div>
                          <div className="text-linear-text-muted text-xs ml-2">Terminal Output</div>
                        </div>
                        <div className="bg-linear-bg-input text-linear-text p-5 font-mono text-sm leading-relaxed">
                          <div className="text-success">Generating test cases...</div>
                          <div className="mt-3 text-linear-accent">TEST CASE: TC-001</div>
                          <div className="text-linear-text-secondary">Title: Verify successful login with valid credentials</div>
                          <div className="text-warning">Priority: High</div>
                          <div className="mt-2 text-linear-text-secondary">Steps:</div>
                          <div className="ml-4 text-linear-text-muted">1. Navigate to login page</div>
                          <div className="ml-4 text-linear-text-muted">2. Enter valid username</div>
                          <div className="ml-4 text-linear-text-muted">3. Enter valid password</div>
                          <div className="ml-4 text-linear-text-muted">4. Click Login button</div>
                          <div className="mt-2 text-success">Expected: User is redirected to dashboard</div>
                          <div className="mt-2 text-linear-accent animate-pulse">|</div>
                        </div>
                      </div>
                    </div>

                    {/* Test Case Standards */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Test Case Standards
                      </h3>
                      <p className="text-linear-text-secondary mb-4 text-sm">
                        Every generated test case follows the Test Case Management Handbook standards:
                      </p>
                      <div className="space-y-3 mb-4">
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Naming Convention</div>
                          <p className="text-sm text-linear-text-muted mb-2">
                            Titles follow the pattern: <code className="bg-linear-bg-card px-1.5 py-0.5 rounded text-linear-accent">Feature &gt; Specific Test Scenario</code>
                          </p>
                          <div className="text-xs text-linear-text-muted">
                            <span className="text-success">✓ Good:</span> "Offer Carousel &gt; Navigate Between Slides Using Arrow Buttons"
                            <br />
                            <span className="text-error">✗ Bad:</span> "Test navigation" or "Check offers page"
                          </div>
                        </div>
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Specific Prerequisites</div>
                          <p className="text-sm text-linear-text-muted">
                            Prerequisites are actionable and specific, not vague. Instead of "User is logged in", you'll see "User is authenticated on Chrome desktop browser".
                          </p>
                        </div>
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Paired Steps & Expected Results</div>
                          <p className="text-sm text-linear-text-muted">
                            Each step has a specific, observable expected result. No vague "works correctly" assertions. Instead you get concrete outcomes like "Red error banner displays with message 'Invalid ZIP code'".
                          </p>
                        </div>
                      </div>

                      {/* Validation Warnings */}
                      <div className="bg-info/10 rounded-lg p-4 border border-info/30">
                        <div className="flex gap-3">
                          <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <div className="font-semibold text-info mb-1">Automatic Validation</div>
                            <p className="text-sm text-linear-text-secondary">
                              Generated test cases get validated against handbook standards automatically. You might see warnings for vague prerequisites or ambiguous expected results. Use those flags to tighten up quality during your review pass.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Selective Export */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Selective Export
                      </h3>
                      <p className="text-linear-text-secondary mb-4 text-sm">
                        You don't have to export everything. Each test case has a checkbox on its left side, so you can pick exactly which ones you want.
                      </p>
                      <div className="space-y-3 text-sm text-linear-text-secondary">
                        <p>Say you already have 5 tests written by hand and the generator creates 10. Check the 5 new ones, uncheck the rest, and export just those. The Export button shows a count when you've got a partial selection, like "Export (5)".</p>
                        <p>The "All selected" checkbox at the top of the list toggles everything at once. You can also deselect all and then check just the ones you want.</p>
                      </div>
                      <div className="mt-4 bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                        <p className="text-xs text-linear-text-muted">Available formats: TestRail CSV, Zephyr Scale CSV, Jira CSV, Markdown, and JSON. Inline edits you've made are included in the export.</p>
                      </div>
                    </div>

                    {/* [VERIFY] Tags */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        [VERIFY] Tags
                      </h3>
                      <div className="space-y-3 text-sm text-linear-text-secondary">
                        <p>When the AI can't pin down the expected behavior from your requirements, it picks the most likely outcome and marks the step with an amber <span className="px-1 py-0.5 rounded bg-warning/15 text-warning text-xs font-medium">[VERIFY]</span> tag. Think of it as the AI raising its hand: "I went with this, but you should double-check."</p>
                        <p>These tags show up as highlighted badges in the expected result column. Click one to jump straight into edit mode for that test case, where you can replace the uncertain result with the right answer.</p>
                        <p>A banner at the top of your results tells you how many test cases need attention. You can also use the "Show [VERIFY] only" filter to focus on just the flagged items.</p>
                        <p>When you export, all [VERIFY] tags are stripped automatically. Your exported file will be clean regardless of whether you resolved them or not.</p>
                      </div>
                    </div>

                    {/* Note */}
                    <div className="bg-warning/10 rounded-lg p-4 border border-warning/30">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <div className="font-semibold text-warning mb-1">Review Required</div>
                          <p className="text-sm text-linear-text-secondary">
                            You should review and customize generated test cases for your specific testing requirements and environment. The AI gives you a solid starting point, but your QA expertise is what makes the difference between good and thorough coverage.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Retention Info */}
                    <div className="bg-info/10 rounded-lg p-4 border border-info/30">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <div className="font-semibold text-info mb-1">Data Retention</div>
                          <p className="text-sm text-linear-text-secondary">
                            Test suites stick around for <strong>90 days</strong> by default. Star a suite to keep it forever.
                            But be aware: if you unstar a suite that's older than 90 days, it gets removed right away.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Common Workflows */}
              <section id="workflows" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-info/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Common Workflows</h2>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    {[
                      {
                        title: 'Sprint Planning Preparation',
                        steps: [
                          'Import sprint tickets directly using the Jira field (search by keyword or paste ticket keys)',
                          'Toggle on descriptions and attachments for richer context',
                          'Add application context (name and URL) in the text area if needed',
                          'Generate test cases',
                          'Review and refine what comes out',
                          'Export the CSV into your test management system',
                          'Assign test cases to the sprint',
                        ],
                      },
                      {
                        title: 'Jira Ticket Import',
                        steps: [
                          'Type a ticket key (e.g. PROJ-123), search term, or Jira URL into the Jira field',
                          'Browse the dropdown results and check the summary, status and type',
                          'Toggle on Description and/or Attachments for the ticket you want',
                          'Click "Import Ticket" to lock in your selection',
                          'Add text context or screenshots alongside the ticket if you want to guide the AI further',
                          'Set your output preferences (count, detail level, devices) and hit Generate',
                        ],
                      },
                      {
                        title: 'Requirements Document Processing',
                        steps: [
                          'Grab the PRD or requirements doc (PDF)',
                          'Upload it to the Test Case Generator',
                          'Add application context',
                          'Generate a full test suite',
                          'Download the CSV output',
                          'Check for coverage gaps',
                          'Add edge cases where needed',
                        ],
                      },
                      {
                        title: 'Fork & Iterate',
                        steps: [
                          'Generate an initial set of test cases',
                          'Review results and note what\'s missing or off',
                          'Click the fork icon in the results header',
                          'The form reopens pre-filled with your original requirement, Jira ticket, Figma URL and all settings',
                          'Refine requirements, adjust count or detail level',
                          'Regenerate until you\'re happy with coverage',
                        ],
                      },
                      {
                        title: 'Inline Editing',
                        steps: [
                          'Generate or open test cases from the Previous Generations panel (click the clock icon in the header)',
                          'Expand any test case by clicking its header',
                          'Click the pencil icon to enter edit mode',
                          'Change the title, preconditions or individual steps directly',
                          'Use the up/down arrows next to each step to reorder them. The arrows gray out at the boundaries',
                          'Add new steps with the "+ Add Step" button, or delete steps with the X button',
                          'Click "Save" to persist your changes, or "Cancel" to discard',
                          'Your edits are included when you export, so the file reflects exactly what you see on screen',
                        ],
                      },
                      {
                        title: 'Resolving [VERIFY] Tags',
                        steps: [
                          'After generation, look for the amber banner that tells you how many items need review',
                          'Click "Show [VERIFY] only" to filter down to just the flagged test cases',
                          'Click any amber [VERIFY] badge to enter edit mode for that test case',
                          'Replace the uncertain expected result with the correct one',
                          'Save your changes. The verify counter updates as you go',
                          'Export when done. Any remaining tags are stripped from the output automatically',
                        ],
                      },
                      {
                        title: 'Star & Templates',
                        steps: [
                          'Generate or open a set of test cases',
                          'Click the star icon in the results header to mark the suite as a template',
                          'Starred suites are kept forever. Unstarred ones expire after 90 days',
                          'Click the clock icon to open Previous Generations, then use the star filter to see only starred suites',
                          'Use starred templates as starting points and fork them for new projects',
                        ],
                      },
                      {
                        title: 'Previous Generations Panel',
                        steps: [
                          'Click the clock icon in the header bar to slide open the Previous Generations panel from the right',
                          'Toggle between "My Test Cases" and "All" to filter by ownership',
                          'Use the star filter to narrow down to starred/template suites',
                          'Hit the refresh button to reload the list',
                          'Select any suite to open its detail view with full date, time and timezone (e.g., "Jan 15, 2026, 2:30 PM PT")',
                          'Close the panel with the X button or press Escape',
                        ],
                      },
                      {
                        title: 'Quick Smoke Test Generation',
                        steps: [
                          'Identify your critical user flows',
                          'Write brief descriptions of each one',
                          'Set the test case count to 3 or 5 and detail level to Concise',
                          'Generate focused test cases',
                          'Export and add them to your regression suite',
                        ],
                      },
                    ].map((workflow, idx) => (
                      <div key={idx} className="bg-linear-bg-input rounded-lg p-5 border border-linear-border">
                        <h3 className="font-semibold text-linear-text mb-4 flex items-center gap-2">
                          <span className="w-6 h-6 bg-linear-accent rounded flex items-center justify-center text-white text-xs font-bold">
                            {idx + 1}
                          </span>
                          {workflow.title}
                        </h3>
                        <ol className="space-y-2">
                          {workflow.steps.map((step, stepIdx) => (
                            <li key={stepIdx} className="flex items-start gap-3 text-sm text-linear-text-muted">
                              <span className="flex-shrink-0 w-5 h-5 bg-linear-border rounded-full flex items-center justify-center text-xs font-medium text-linear-text-secondary">
                                {stepIdx + 1}
                              </span>
                              <span className="pt-0.5">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Golden Examples */}
              <section id="golden-examples" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-warning/15 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-warning" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M5 2h14a1 1 0 011 1v19.143a.5.5 0 01-.766.424L12 18.03l-7.234 4.536A.5.5 0 014 22.143V3a1 1 0 011-1z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Saving Golden Examples</h2>
                  </div>

                  <div className="space-y-6">
                    {/* What It Is */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        What Are Golden Examples?
                      </h3>
                      <p className="text-linear-text-secondary text-sm leading-relaxed">
                        Golden examples are individual test cases you've marked as high quality. When you save one, the AI automatically uses it as a style reference the next time you generate test cases. Think of them as a way to show the AI what "good" looks like for your team, so it matches your naming style, step detail, precondition depth and tag patterns without you having to explain it every time.
                      </p>
                    </div>

                    {/* How to Save */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        How to Save Examples
                      </h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          'Click the bookmark icon on any individual test case in the results to save it as a golden example',
                          'Or select multiple test cases using the checkboxes and click "Save as Examples" to save them in bulk',
                          'Already-saved examples show a filled amber bookmark so you can tell at a glance which ones are saved',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* How It Works */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        How It Works Behind the Scenes
                      </h3>
                      <div className="space-y-3">
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Automatic Prompt Injection</div>
                          <p className="text-sm text-linear-text-muted">
                            Saved examples are automatically included in the AI prompt when you generate new test cases. You don't need to toggle anything on or configure it in the main form. It happens in the background every time.
                          </p>
                        </div>
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Style Matching</div>
                          <p className="text-sm text-linear-text-muted">
                            The AI reads your golden examples and matches their naming conventions, step-level detail, precondition depth and tag patterns. If your examples use "Login Page &gt; Verify Error on Invalid Credentials" as a title format, new test cases will follow that same pattern.
                          </p>
                        </div>
                        <div className="bg-linear-bg-input rounded-lg p-4 border border-linear-border">
                          <div className="font-medium text-linear-text mb-2">Selection Per Generation</div>
                          <p className="text-sm text-linear-text-muted">
                            You can save up to 20 golden examples total, and 5 are included in each generation. The system picks a representative sample so the AI gets a balanced view of your style preferences.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Managing Examples */}
                    <div>
                      <h3 className="text-base font-semibold text-linear-text mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        Managing Your Examples
                      </h3>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          'Go to Settings to view all your saved golden examples in one place',
                          'Remove outdated examples using the trash icon next to each one',
                          'You can save a maximum of 20 examples. If you hit the limit, remove older ones before adding new ones',
                        ].map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-success mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-linear-text-muted text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="bg-info/10 rounded-lg p-4 border border-info/30">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-info flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <div>
                          <div className="font-semibold text-info mb-2">Tips for Golden Examples</div>
                          <ul className="space-y-2 text-sm text-linear-text-secondary">
                            <li>Save 3 to 5 diverse examples for best results. Mix happy path, sad path and edge case test cases so the AI has a range of styles to reference.</li>
                            <li>This feature is most useful early on while you're calibrating the AI output to match your team's conventions. Once the AI consistently produces what you want, you may not need to add more.</li>
                            <li>Remove examples that become outdated as your testing standards evolve. Stale examples can push the AI toward patterns you've moved past.</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Tips */}
              <section id="tips" className="scroll-mt-20">
                <div className="card">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-display font-bold text-linear-text">Tips for Better Results</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        title: 'Be Detailed',
                        desc: 'The more detail you feed in, the better the test cases come out. Include edge cases and error scenarios in your requirements.',
                      },
                      {
                        title: 'List Specific Scenarios',
                        desc: 'If you spell out scenarios in your input (e.g. "Test these 4 flows: ..."), the AI generates a test case for each one. Don\'t be shy about being prescriptive.',
                      },
                      {
                        title: 'Specify the Platform',
                        desc: 'Mention "mobile app", "web application" or "API" in your input. The AI adapts its test language to match. If you don\'t specify, tests stay platform-neutral.',
                      },
                      {
                        title: 'Adjust Count, Detail & Custom Instructions',
                        desc: 'Use Output Settings to set the right scope. Flip on "Let AI decide" when you\'re not sure how many test cases you need, and the AI will match the count to your requirement complexity. Or use the slider when you know the exact number you want. Pick target devices and browsers for platform-specific steps. Try 3 to 5 Concise cases for smoke tests, or 15 to 20 Detailed cases for full coverage. Custom Instructions can override defaults like step count or scenario combining.',
                      },
                      {
                        title: 'Fork to Iterate',
                        desc: 'Not happy with results? Click the fork icon to go back to the form with everything pre-filled. Tweak your requirements and regenerate.',
                      },
                      {
                        title: 'Combine Inputs',
                        desc: 'Jira tickets plus screenshots produce the best output. Import a ticket for structured requirements and add a screenshot so the AI has visual context too. Text input on top of that works great for extra guidance.',
                      },
                      {
                        title: 'Working with Figma Rate Limits',
                        desc: 'Figma caps how many design fetches one token can make in a minute. If you hit the limit, the app waits the exact number of seconds Figma asks for and retries automatically. If the retry still fails, the banner shows a countdown and a Try again button. Linking to a specific node (Copy link to selection in Figma) is cheaper than sharing the whole file URL, so that is the first thing to switch to if you keep running into the cap.',
                      },
                      {
                        title: 'Edit Inline',
                        desc: 'Spotted a typo or want to tweak a step? Expand the test case, click the pencil icon, make your changes and hit Save. No need to regenerate the whole suite.',
                      },
                      {
                        title: 'Star Your Best Suites',
                        desc: 'Star high-quality test suites to save them as templates. Starred suites stay around forever, but unstarred ones get cleaned up after 90 days.',
                      },
                      {
                        title: 'Share with Your Team',
                        desc: 'Hit the Share button to generate a link anyone on your team can view. They can fork it to create their own variations.',
                      },
                      {
                        title: 'Review and Customize',
                        desc: 'Always review what gets generated. The AI gives you a strong starting point, but your QA expertise is what turns it into real coverage.',
                      },
                      {
                        title: 'Apply Your Expertise',
                        desc: 'This tool speeds up test case creation but it doesn\'t replace what you know. Apply your domain knowledge to fill gaps and catch things the AI missed.',
                      },
                    ].map((tip, idx) => (
                      <div key={idx} className="bg-linear-bg-input rounded-lg p-4 border border-linear-border hover:border-linear-border-input transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-linear-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-4 h-4 text-linear-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="font-semibold text-linear-text mb-1 text-sm">{tip.title}</h3>
                            <p className="text-xs text-linear-text-muted leading-relaxed">{tip.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Final Important Note */}
                  <div className="mt-6 bg-warning/10 rounded-lg p-5 border border-warning/30">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-warning/20 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold text-warning mb-2">Human Review Required</h3>
                        <p className="text-linear-text-secondary text-sm leading-relaxed">
                          This tool speeds up test case creation, but it doesn't replace your QA expertise. Review, validate and customize generated test cases for your specific application, test environment and org standards. When generating from Figma designs or images, double-check that any specific text, numbers, or labels referenced in test cases match what's actually in your design. Your domain knowledge is what catches the gaps the AI can't see.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            </div>
          </main>
        </div>
      </div>
    </AppLayout>
  )
}
